import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'react-bootstrap-v5';
import {
  KTSVG,
  SimpleAlert,
  formatPrice,
} from '../../../../../_mexablock/helpers';
import { NumericFormat } from 'react-number-format';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { StepperComponent } from '../../../../../_mexablock/assets/ts/components';
import {
  validateICCID,
  validateEmail,
} from '../../../../service/operacion/Validacion';
import * as auth from '../../../../modules/auth/redux/AuthRedux';
import { useSelectOffers } from '../hooks/ActivacionHook';
import { createActivation } from '../../../../service/operacion/Activaciones';
import { getUserByToken } from '../../../../modules/auth/redux/AuthCRUD';

type Props = {
  show: boolean;
  handleClose: (updateData: boolean) => void;
};

const schemaNewActivationHogar = [
  Yup.object({
    first_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    mother_last_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    father_last_name: Yup.string()
      .max(20, 'Máximo 20 caracteres')
      .required('Requerido.'),
    email: Yup.string()
      .email('Formato de correo inválido.')
      .min(3, 'Mínimo 3 caracteres.')
      .max(50, 'Máximo 50 caracteres.')
      .required('Requerido.'),
  }),
  Yup.object({
    iccid: Yup.string()
      .required('Requerido.')
      .min(19, 'Formato de ICCID inválido')
      .max(19, 'Formato de ICCID inválido'),
    calle: Yup.string().required('Requerido.'),
    colonia: Yup.string().required('Requerido.'),
    municipio: Yup.string().required('Requerido.'),
    cp: Yup.string().required('Requerido.'),
    estado: Yup.string().required('Requerido.'),
    coordenadas: Yup.string().required('Requerido.'),
  }),
  Yup.object({
    offer_id: Yup.number().min(1, 'Requerido.').required('Requerido.'),
  }),
];

let maxDate = new Date();
maxDate.setDate(maxDate.getDate() + 1);

const initValues = {
  iccid: '',
  calle: '',
  colonia: '',
  municipio: '',
  cp: '',
  estado: '',
  coordenadas: '',
  schedule_date: maxDate,
  offer_id: 0,
  inmediality: 1,
  first_name: '',
  mother_last_name: '',
  father_last_name: '',
  email: '',
};

const NewActivationHogarModal: React.FC<Props> = ({ show, handleClose }) => {
  const dispatch = useDispatch();
  const { data: offers } = useSelectOffers(show, 'activation_ho');
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [textStepper, setTextStepper] = useState('Continuar');
  const [currentSchema, setCurrentSchema] = useState(schemaNewActivationHogar[0]);
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(
      stepperRef.current as HTMLDivElement
    );
  };

  useEffect(() => {
    if (show) {
      loadStepper();
    }
  }, [stepperRef, show]);

  const formNewActivationHogar = useFormik({
    initialValues: initValues,
    validationSchema: currentSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!stepper.current) {
        return;
      }
      switch (stepper.current.currentStepIndex) {
        case 1:
          setLoading(true);
          setTimeout(() => {
            validateEmail(values)
              .then(async (res: any) => {
                if (!stepper.current) {
                  return;
                }
                const { disponible, mensaje } = res.data.doc.data;
                if (disponible) {
                  stepper.current.goNext();
                  setCurrentSchema(schemaNewActivationHogar[1]);
                  setLoading(false);
                } else {
                  SimpleAlert(mensaje, 3000, 'error');
                  setLoading(false);
                }
              })
              .catch((err: any) => {
                const message = err.response?.data?.message || err.message;
                SimpleAlert(message, 3000, 'error');
                setLoading(false);
              });
          }, 1000);
          break;
        case 2:
          setLoading(true);
          setTimeout(() => {
            validateICCID({ ...values, type: 'activation_ho' })
              .then(async (res) => {
                const { success, error, activation } = res.data.doc.data;
                if (!activation) {
                  if (!stepper.current) {
                    return;
                  }
                  if (success) {
                    stepper.current.goNext();
                    setCurrentSchema(schemaNewActivationHogar[2]);
                    setLoading(false);
                    setTextStepper('Activar');
                  } else {
                    SimpleAlert(error || 'Este ICCID no está registrado para Internet Hogar.', 3000, 'error');
                    setLoading(false);
                  }
                } else {
                  SimpleAlert('ICCID YA ACTIVO.', 3000, 'error');
                  setLoading(false);
                }
              })
              .catch(() => {
                SimpleAlert(
                  'Error al validar el ICCID. Verifica que esté registrado en Inventario para Internet Hogar.',
                  3000,
                  'error'
                );
                setLoading(false);
              });
          }, 1000);
          break;
        case 3:
          setLoading(true);
          setTimeout(() => {
            const body = {
              ...values,
              product_type: 'hogar',
              address: values.coordenadas,
            };
            createActivation(body)
              .then(async (res: any) => {
                if (!stepper.current) {
                  return;
                }
                const msisdn = res.data?.doc?.data?.msisdn || '';
                stepper.current.goNext();
                setTextStepper('Finalizar');
                setPhoneNumber(msisdn);
                SimpleAlert(
                  'La activación de Internet Hogar se procesó exitosamente',
                  3000,
                  'success'
                );
                setLoading(false);

                // UPDATE SALDO
                const user = await getUserByToken();
                dispatch(auth.actions.fulfillUser(user));
              })
              .catch((err: any) => {
                let resMessageToast: string = '';
                if (err.response?.data?.message) {
                  resMessageToast = err.response.data.message;
                } else if (err.request) {
                  resMessageToast =
                    'Error de red: Verifique su conexión a Internet';
                } else {
                  resMessageToast = err.message || 'Error desconocido';
                }
                SimpleAlert(resMessageToast, 3000, 'error');
                setLoading(false);
              });
          }, 1000);
          break;
        case 4:
          if (!stepper.current) {
            return;
          }
          handleClose(true);
          stepper.current.goto(1);
          setTextStepper('Continuar');
          setPhoneNumber('');
          setCurrentSchema(schemaNewActivationHogar[0]);
          formNewActivationHogar.resetForm();
          setLoading(false);
          break;
      }
    },
  });

  const prevStep = () => {
    if (!stepper.current) {
      return;
    }
    stepper.current?.goPrev();
    const prevIndex = stepper.current.currentStepIndex - 1;
    if (prevIndex >= 0 && schemaNewActivationHogar[prevIndex]) {
      setCurrentSchema(schemaNewActivationHogar[prevIndex]);
    }
    switch (stepper.current.currentStepIndex) {
      case 1:
        setTextStepper('Continuar');
        break;
      case 2:
        setTextStepper('Continuar');
        break;
      case 3:
        setTextStepper('Activar');
        break;
      case 4:
        setTextStepper('Finalizar');
        break;
    }
  };

  const handleChangeInput = (e: any, inputName: string) => {
    formNewActivationHogar.setFieldValue(inputName, e.target.value);
  };

  const handleOnChange = (e: any) => {
    const radioValue = Number(e.target.value);
    formNewActivationHogar.setFieldValue('inmediality', radioValue);
  };

  return (
    <Modal
      id="kt_modal_new_activation_hogar"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered mw-900px"
      show={show}
      onHide={() => handleClose(false)}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            data-bs-dismiss="modal"
            onClick={() => handleClose(false)}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Nueva Activación - Internet Hogar</h1>
          </div>
          <div
            ref={stepperRef}
            className="stepper stepper-links d-flex flex-column gap-5 first"
            id="kt_modal_create_app_stepper"
          >
            <div className="stepper-nav justify-content-center py-2">
              <div
                className="stepper-item current"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Datos del Cliente</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Datos de la Activación</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Ofertas</h3>
              </div>
              <div
                className="stepper-item pending"
                data-kt-stepper-element="nav"
              >
                <h3 className="stepper-title">Completada</h3>
              </div>
            </div>
            <form
              className="mx-auto w-100 mw-800px pt-4 pb-4"
              id="kt_modal_top_up_wallet_stepper_form"
              autoComplete="off"
              onSubmit={formNewActivationHogar.handleSubmit}
            >
              {/* PASO 1: DATOS DEL CLIENTE */}
              <div
                className="current"
                data-kt-stepper-element="content"
              >
                <div className="w-100 ms-20 me-20 h-auto">
                  <div className="fv-row mb-7 fv-plugins-icon-container">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Nombre
                          </label>
                          {formNewActivationHogar.touched.first_name &&
                            formNewActivationHogar.errors.first_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationHogar.errors.first_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Nombre"
                          {...formNewActivationHogar.getFieldProps('first_name')}
                          className="form-control mb-3"
                          type="text"
                          name="first_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'first_name');
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Apellido paterno
                          </label>
                          {formNewActivationHogar.touched.father_last_name &&
                            formNewActivationHogar.errors.father_last_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationHogar.errors.father_last_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Apellido paterno"
                          {...formNewActivationHogar.getFieldProps(
                            'father_last_name'
                          )}
                          className="form-control mb-3"
                          type="text"
                          name="father_last_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'father_last_name');
                          }}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Apellido materno
                          </label>
                          {formNewActivationHogar.touched.mother_last_name &&
                            formNewActivationHogar.errors.mother_last_name && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationHogar.errors.mother_last_name}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Apellido materno"
                          {...formNewActivationHogar.getFieldProps(
                            'mother_last_name'
                          )}
                          className="form-control mb-3"
                          type="text"
                          name="mother_last_name"
                          autoComplete="off"
                          onChange={(e: any) => {
                            handleChangeInput(e, 'mother_last_name');
                          }}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="w-100 d-flex">
                          <label className="required fs-6 fw-semibold form-label mb-2">
                            Correo electrónico
                          </label>
                          {formNewActivationHogar.touched.email &&
                            formNewActivationHogar.errors.email && (
                              <div className="fv-plugins-message-container text-danger w-auto ms-5">
                                <span role="alert">
                                  {formNewActivationHogar.errors.email}
                                </span>
                              </div>
                            )}
                        </div>
                        <input
                          placeholder="Correo electrónico"
                          {...formNewActivationHogar.getFieldProps('email')}
                          className="form-control mb-3 mb-lg-0"
                          type="text"
                          name="email"
                          autoComplete="off"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* PASO 2: ACTIVACIÓN HOGAR (ICCID + DOMICILIO + COORDENADAS) */}
              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100 ms-20 me-20 h-auto">
                  <div className="fv-row mb-6 fv-plugins-icon-container">
                    <div className="w-100 d-flex">
                      <label className="required fs-6 fw-semibold form-label mb-2">
                        ICCID (Internet Hogar)
                      </label>
                      {formNewActivationHogar.touched.iccid &&
                        formNewActivationHogar.errors.iccid && (
                          <div className="fv-plugins-message-container text-danger w-auto ms-5">
                            <span role="alert">
                              {formNewActivationHogar.errors.iccid}
                            </span>
                          </div>
                        )}
                    </div>

                    <NumericFormat
                      className="form-control mb-2"
                      {...formNewActivationHogar.getFieldProps('iccid')}
                      decimalScale={0}
                      maxLength={19}
                      fixedDecimalScale
                      placeholder="Identificador ICCID de 19 dígitos"
                      prefix=""
                      onValueChange={(vals: any) => {
                        formNewActivationHogar.setFieldValue('iccid', vals.floatValue);
                      }}
                    />
                  </div>

                  <div className="mt-6 border-top pt-5">
                    <div className="d-flex align-items-center mb-3">
                      <span className="fs-5 fw-bold text-gray-800">
                        📍 Domicilio de Instalación
                      </span>
                    </div>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Calle y número
                        </label>
                        <input
                          placeholder="Ej. Av. Insurgentes Sur 1234"
                          {...formNewActivationHogar.getFieldProps('calle')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.calle &&
                          formNewActivationHogar.errors.calle && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.calle}
                            </div>
                          )}
                      </div>
                      <div className="col-md-6">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Colonia
                        </label>
                        <input
                          placeholder="Ej. Del Valle"
                          {...formNewActivationHogar.getFieldProps('colonia')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.colonia &&
                          formNewActivationHogar.errors.colonia && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.colonia}
                            </div>
                          )}
                      </div>
                      <div className="col-md-6">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Municipio / Alcaldía
                        </label>
                        <input
                          placeholder="Ej. Benito Juárez"
                          {...formNewActivationHogar.getFieldProps('municipio')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.municipio &&
                          formNewActivationHogar.errors.municipio && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.municipio}
                            </div>
                          )}
                      </div>
                      <div className="col-md-6">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Código Postal (CP)
                        </label>
                        <input
                          placeholder="Ej. 03100"
                          maxLength={5}
                          {...formNewActivationHogar.getFieldProps('cp')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.cp &&
                          formNewActivationHogar.errors.cp && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.cp}
                            </div>
                          )}
                      </div>
                      <div className="col-md-6">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Estado
                        </label>
                        <input
                          placeholder="Ej. Ciudad de México"
                          {...formNewActivationHogar.getFieldProps('estado')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.estado &&
                          formNewActivationHogar.errors.estado && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.estado}
                            </div>
                          )}
                      </div>
                      <div className="col-12">
                        <label className="required fs-7 fw-semibold form-label mb-1">
                          Coordenadas (Latitud, Longitud)
                        </label>
                        <input
                          placeholder="Ej. 19.4394164, -99.1870695"
                          {...formNewActivationHogar.getFieldProps('coordenadas')}
                          className="form-control form-control-solid fs-7"
                          type="text"
                        />
                        {formNewActivationHogar.touched.coordenadas &&
                          formNewActivationHogar.errors.coordenadas && (
                            <div className="text-danger fs-8 mt-1">
                              {formNewActivationHogar.errors.coordenadas}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>


                </div>
              </div>

              {/* PASO 3: OFERTAS HOGAR */}
              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100 px-4 h-auto">
                  <div className="fv-row mb-7 fv-plugins-icon-container">
                    <div className="w-100 d-flex justify-content-center mb-3">
                      <label className="required fs-5 fw-bold form-label mb-2 text-center">
                        Selecciona un paquete Internet Hogar
                      </label>
                      {formNewActivationHogar.touched.offer_id &&
                        formNewActivationHogar.errors.offer_id && (
                          <div className="fv-plugins-message-container text-danger w-auto ms-3">
                            <span role="alert">
                              {formNewActivationHogar.errors.offer_id}
                            </span>
                          </div>
                        )}
                    </div>
                    {offers.length === 0 && (
                      <div className="alert alert-warning p-4 text-center">
                        No hay ofertas de Internet Hogar disponibles en este momento.
                      </div>
                    )}
                    <div className="row g-4 justify-content-center mt-1">
                      {offers.map((item: any, index: number) => (
                        <div className="col-12 col-sm-6 col-md-4" key={index}>
                          <a
                            href="#/"
                            className={clsx(
                              'btn btn-outline btn-flex flex-column flex-stack p-5 w-100 h-100 border-hover-primary text-center',
                              {
                                'btn-active-light-primary active border-primary':
                                  formNewActivationHogar.values.offer_id === item.id,
                              }
                            )}
                            style={{
                              minHeight: '160px',
                              border: 'solid 1px #dbdfe9',
                              borderRadius: '12px',
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              formNewActivationHogar.setFieldValue(`offer_id`, item.id);
                            }}
                          >
                            <div className="text-center w-100">
                              <span
                                className={clsx(
                                  'fw-bold fs-2 d-block text-hover-primary',
                                  {
                                    'text-primary':
                                      formNewActivationHogar.values.offer_id === item.id,
                                    'text-gray-800':
                                      formNewActivationHogar.values.offer_id !== item.id,
                                  }
                                )}
                              >
                                {`${item.gb} GB`}
                              </span>
                              <div className="mt-1">
                                <span
                                  className={clsx('fw-semibold fs-5', {
                                    'text-primary':
                                      formNewActivationHogar.values.offer_id === item.id,
                                    'text-gray-600':
                                      formNewActivationHogar.values.offer_id !== item.id,
                                  })}
                                >
                                  {`${formatPrice(item.price, 'es-MX', 'MXN', 2, 2)}`}
                                </span>
                                <span
                                  className={clsx('fw-semibold fs-7', {
                                    'text-primary':
                                      formNewActivationHogar.values.offer_id === item.id,
                                    'text-gray-500':
                                      formNewActivationHogar.values.offer_id !== item.id,
                                  })}
                                >
                                  {item.commercial_time || item.days 
                                    ? ` / ${item.commercial_time || item.days} días` 
                                    : ''}
                                </span>
                              </div>
                            </div>

                            <div className="badge badge-light-success fw-bolder fs-8 px-3 py-1 mt-3">
                              ⚡ {item.speed || '100 Mbps'} de velocidad
                            </div>
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* PASO 4: COMPLETADA */}
              <div
                data-kt-stepper-element="content"
                className="pending"
              >
                <div className="w-100 ms-20 me-20 h-auto">
                  <div className="fv-row mb-7 text-center">
                    <div
                      className="mx-auto mb-4 d-flex align-items-center justify-content-center"
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        backgroundColor: '#14b8a6',
                        color: '#fff',
                        fontSize: '32px',
                      }}
                    >
                      ✓
                    </div>
                    <h2 className="fs-2x fw-bolder text-gray-900 mb-2">
                      Activación Hogar Completada
                    </h2>
                    <p className="text-gray-500 fs-5 mb-7">
                      La línea de Internet Hogar se ha activado exitosamente.
                    </p>
                    {phoneNumber && (
                      <div className="card bg-light-primary p-4 mw-400px mx-auto mb-6">
                        <span className="text-muted fs-7 text-uppercase fw-bold">Número asignado:</span>
                        <span className="fs-3 fw-bolder text-primary mt-1">{phoneNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ACCIONES DEL WIZARD */}
              <div className="d-flex flex-stack pt-5">
                <div className="me-2">
                  {phoneNumber === '' && (
                    <button
                      onClick={prevStep}
                      type="button"
                      className="btn btn-lg btn-light-primary me-3"
                      data-kt-stepper-action="previous"
                    >
                      <KTSVG
                        path="/media/icons/duotune/arrows/arr063.svg"
                        className="svg-icon-4 me-1"
                      />
                      Regresar
                    </button>
                  )}
                </div>

                <div>
                  <button
                    type="submit"
                    className="btn btn-lg btn-primary me-3"
                    disabled={loading}
                  >
                    {!loading && (
                      <>
                        <span className="indicator-label">{textStepper}</span>
                        {textStepper === 'Continuar' && (
                          <KTSVG
                            path="/media/icons/duotune/arrows/arr064.svg"
                            className="svg-icon-4 me-1 ms-1"
                          />
                        )}
                      </>
                    )}
                    {loading && (
                      <span
                        className="indicator-progress"
                        style={{ display: 'block' }}
                      >
                        Espere por favor...
                        <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export { NewActivationHogarModal };
